/// REACT ///
import { MouseEvent as ReactMouseEvent } from "react";
import { Fragment } from "react";
import { useRef } from "react";
import { useEffect } from "react";
import { useState } from "react";
/// HOOKS ///
import { useAppDispatch } from "api/hooks/apiHook";
import { useAppSelector } from "api/hooks/apiHook";
import useDrawSeams from "hooks/draw_seams";
/// TYPES ///
import { Counter } from "api/types/sketch";
import { CutShape } from "api/types/sketch";
import { Slab } from "api/types/quote";
import { Seam } from "api/types/sketch";
import { Point } from "api/types/sketch";
import { MouseData } from "types/mouse_position";
import { Vector2 } from "api/types/sketch";
/// ACTIONS ///
import { createBulkCutShapes } from "api/actions/sketch/cut_shape";
import { updateLocalCutShapes } from "api/actions/sketch/cut_shape";
import { updateLocalCounter } from "api/actions/sketch/counter";
import { updateCounter } from "api/actions/sketch/counter";
/// MUI ///
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import ShopSeamDrawer from "./drawer";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
/// FUNCTIONS ///
import { createCutShapes } from "functions/sketch";
import { getFirstSlabInAreas } from "functions/sketch/get/slab";
import { getCanvas } from "functions/sketch/context";
import { getDistance } from "functions/sketch/math";
/// STYLES ///
import { canvasStyle } from "styles/sketch";
import { sketchAreaStyle } from "styles/sketch";

interface Props {
  open: boolean,
  setOpen: (open: boolean) => void,
  mouse_data: MouseData,
  selected_counter: Counter | null,
  setSelectedCounter: (counter: Counter | null) => void
}

const MIN_SEAM_EDGE_MARGIN = 1;
const MIN_SEAM_GAP = 1;

function sanitizePointSeams(point: Point, segment_distance: number): Seam[] {
  const seams: Seam[] = point.seams ?? [];
  const corner_seams: Seam[] = seams.filter(item => item.is_on_corner);
  const linear_seams: Seam[] = seams
    .filter(item => !item.is_on_corner && typeof item.distance_from_point === "number")
    .sort((a, b) => (a.distance_from_point ?? 0) - (b.distance_from_point ?? 0));

  const max_distance: number = Math.max(0, segment_distance - MIN_SEAM_EDGE_MARGIN);
  const min_distance: number = Math.min(MIN_SEAM_EDGE_MARGIN, max_distance);

  const accepted_linear: Seam[] = [];
  for (let i = 0; i < linear_seams.length; i++) {
    const seam: Seam = linear_seams[i];
    const raw_distance: number = seam.distance_from_point ?? 0;
    const clamped_distance: number = Math.min(max_distance, Math.max(min_distance, raw_distance));
    const last: Seam | undefined = accepted_linear[accepted_linear.length - 1];
    if (!last || Math.abs((last.distance_from_point ?? 0) - clamped_distance) >= MIN_SEAM_GAP) {
      accepted_linear.push({ ...seam, distance_from_point: clamped_distance });
    }
  }

  return [...corner_seams, ...accepted_linear];
}

export default function StoreDefineSeams({
  open,
  setOpen,
  mouse_data,
  selected_counter,
  setSelectedCounter
}: Props) {
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const { cut_shapes } = useAppSelector((state) => state.sketch);
  const { areas } = useAppSelector((state) => state.quote);
  const [cutting, setCutting] = useState<boolean>(false);
  const [focus_count, setFocusCount] = useState<number>(0);
  const [offset, setOffset] = useState<Vector2>({ X: 0, Y: 0 });

  const active_seam: Seam | null = useDrawSeams(
    mouse_data,
    selected_counter,
    open,
    focus_count,
    offset
  );

  useEffect(() => {
    if (cutting) {
      setSelectedCounter(null);
      setCutting(false);
      setOpen(false);
    }
  }, [cut_shapes, cutting, setOpen, setSelectedCounter]);

  const makeSeams = () => {
    setCutting(true);
    if (selected_counter) {
      const counter: Counter = {
        ...selected_counter,
        points: selected_counter.points.map((point, index, points) => {
          const next_point: Point | undefined = points[index + 1];
          const segment_distance: number = next_point ? getDistance(point.location, next_point.location) : 0;
          return {
            ...point,
            seams: sanitizePointSeams(point, segment_distance)
          };
        })
      };
      const slab: Slab | null = getFirstSlabInAreas(areas, counter.area_uuid);
      let slab_uuid: string = slab ? slab.uuid : areas[0].slabs[0].uuid;
      const cut_shapes: CutShape[] = createCutShapes(
        counter,
        counter.area_uuid,
        slab_uuid,
        counter.points,
        counter.corners,
        [] //offsets
      );
      dispatch(updateLocalCutShapes(cut_shapes, counter.uuid));
      dispatch(createBulkCutShapes(cut_shapes));
      dispatch(updateLocalCounter(counter));
      dispatch(updateCounter(counter));
    }
  }

  const handleFocus = () => {
    if (ref && ref.current) {
      const width = ref.current.scrollWidth;
      const height = ref.current.scrollHeight;
      setOffset({ X: ref.current.getBoundingClientRect().x, Y: ref.current.getBoundingClientRect().y });
      const canvas: HTMLCanvasElement | null = getCanvas(document.getElementById("dialog_canvas"));

      if (canvas) {
        canvas.width = width;
        canvas.height = height;
      }
      setFocusCount(prev => prev + 1);
    }
  }

  const handleEvent = (event: ReactMouseEvent) => {
    if (event.type === "mousedown" && event.button === 0) {
      if (active_seam && selected_counter) {
        setSelectedCounter({
          ...selected_counter, points:
            [...selected_counter.points.map(point => point.uuid === active_seam.point_uuid ?
              {
                ...point, seams: [...point.seams, active_seam].sort(function (a, b) {
                  let x: number | undefined = 0;
                  let y: number | undefined = 0;
                  var i = 0;
                  while (i < point.seams.length) {
                    x = a.distance_from_point;
                    y = b.distance_from_point;
                    i++;
                  }
                  if (x && y) {
                    if (x < y) {
                      return -1;
                    } else if (x > y) {
                      return 1;
                    }
                  }
                  return 0;
                })
              } :
              point
            )]
        });
      }
    }
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xl" onFocus={handleFocus}>
      <DialogTitle>Define Seams</DialogTitle>
      <DialogContent sx={{ marginTop: 2 }}>
        <Grid container>
          <Grid item xs={10.5}>
            <Box
              onContextMenu={(e) => {
                e.preventDefault();
              }}
              sx={[sketchAreaStyle, { height: "75vh" }]}
              onMouseDown={(event: ReactMouseEvent) => handleEvent(event)}
              onMouseUp={(event: ReactMouseEvent) => handleEvent(event)}
              ref={ref}>
              <canvas
                id="dialog_canvas"
                className={JSON.stringify(canvasStyle)}
                style={{ backgroundColor: "#fafafa" }} />
            </Box>
          </Grid>
          <Grid item xs={1.5}>
            <ShopSeamDrawer
              selected_counter={selected_counter}
              setSelectedCounter={setSelectedCounter} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {
          cutting === false ?
            <Fragment>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={makeSeams}>Save</Button>
            </Fragment> :
            <CircularProgress size={22} sx={{ padding: 1, marginRight: 2 }} />
        }
      </DialogActions>
    </Dialog>
  )
}
